import { useCallback, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { CommentThread } from '@/components/comments/comment-thread';
import { DiffWithComments } from '@/components/comments/diff-with-comments';
import type { InlineCommentData } from '@/components/comments/inline-comment';
import { ErrorBoundary } from '@/components/error-boundary/error-boundary';
import { FileTree } from '@/components/pr/file-tree';
import { ApiError, apiClient } from '@/modules/api/api-client';
import { FREQUENT_STALE_TIME } from '@/modules/query/query-client';
import { queryKeys } from '@/modules/query/query-keys';
import { useAuthStore } from '@/stores/auth-store';
import { formatRelativeTime } from '@/utils/format-time';
import { DETAIL_TITLE_MAX, truncateTitle } from '@/utils/truncate';

/** PR detail from GET /repos/:name/prs/:id */
interface PrDetail {
  prId: string;
  title: string;
  description?: string;
  authorId: string;
  authorDisplayName?: string;
  sourceBranch: string;
  destinationBranch: string;
  status: 'OPEN' | 'CLOSED' | 'MERGED';
  createdAt: string;
  lastActivityDate: string;
  approvals: { userId: string; displayName: string; approvedAt: string }[];
  approvalCount: number;
  reviewStatus?: string;
  ciStatus?: string;
  mergedAt?: string;
  mergedBy?: string;
}

/**
 * A single file entry as returned RAW by GET /repos/:name/prs/:id/diff.
 * The backend serialises a bare array of these (no wrapper object), with
 * lowercase `changeType` and a `binary` flag.
 */
interface RawDiffFileEntry {
  filePath: string;
  changeType: string;
  oldFilePath?: string | null;
  /** Unified diff text for this file, produced by `git diff` */
  diffText: string;
  binary?: boolean;
  additions: number;
  deletions: number;
}

/** A single file entry after normalisation into the shape the UI components expect. */
interface DiffFileEntry {
  filePath: string;
  changeType: 'ADDED' | 'MODIFIED' | 'DELETED' | 'RENAMED';
  oldFilePath?: string;
  diffText: string;
  isBinary?: boolean;
  additions: number;
  deletions: number;
}

/** Normalised diff response consumed by the file tree and diff viewer. */
interface DiffResponse {
  files: DiffFileEntry[];
  /** Present when the diff has not yet been computed; the caller should retry */
  notReady?: boolean;
}

/** HTTP 503 code the backend returns while the diff is still being computed. */
const DIFF_NOT_READY_STATUS = 503;

/** Maps the backend's lowercase change type to the uppercase form the UI uses. */
function normaliseChangeType(raw: string): DiffFileEntry['changeType'] {
  switch (raw.toUpperCase()) {
    case 'ADDED':
    case 'MODIFIED':
    case 'DELETED':
    case 'RENAMED':
      return raw.toUpperCase() as DiffFileEntry['changeType'];
    default:
      return 'MODIFIED';
  }
}

/**
 * Fetches and normalises the PR diff.
 *
 * The backend returns a BARE ARRAY of file entries on success (200) and HTTP 503
 * with code DIFF_NOT_READY while the CodeBuild job is still producing the diff.
 * This adapter reconciles that contract with the `{ files, notReady }` shape the
 * page's components expect, and maps field casing (`changeType`, `binary`).
 */
async function fetchDiff(repoName: string, prId: string): Promise<DiffResponse> {
  const path = `/repos/${encodeURIComponent(repoName)}/prs/${encodeURIComponent(prId)}/diff`;
  try {
    const raw = await apiClient.get<RawDiffFileEntry[]>(path);
    const files: DiffFileEntry[] = (raw ?? []).map((f) => ({
      filePath: f.filePath,
      changeType: normaliseChangeType(f.changeType),
      oldFilePath: f.oldFilePath ?? undefined,
      diffText: f.diffText,
      isBinary: f.binary ?? false,
      additions: f.additions,
      deletions: f.deletions,
    }));
    return { files };
  } catch (error) {
    // Diff not yet computed — surface as a retryable not-ready state rather than an error.
    if (error instanceof ApiError && error.status === DIFF_NOT_READY_STATUS) {
      return { files: [], notReady: true };
    }
    throw error;
  }
}

/** CI checks response from GET /repos/:name/prs/:id/checks */
interface ChecksResponse {
  runId: string | null;
  aggregateStatus: string | null;
  triggerEventType: string | null;
  startTimestamp: string | null;
  checks: CheckDetail[];
  message?: string;
}

/** Commit summary from GET /repos/:name/prs/:id/commits */
interface CommitSummary {
  commitId: string;
  message: string;
  authorName: string;
  authorDate: string;
}

/**
 * Resolves the PR's head (source-tip) commit id: the first entry of GET /commits, which
 * the backend returns newest-first (walking back from the source-branch tip).
 *
 * Inline comments must carry this as `commitId` so the Comments service can auto-mark
 * them outdated when a newer commit is pushed (it compares the stored commitId to the
 * PR's latest commit). Reuses the same query key as CommitsSection, so no extra fetch.
 * Returns undefined until the commits query resolves.
 */
function usePrHeadCommitId(repoName: string, prId: string): string | undefined {
  const { data } = useQuery<CommitSummary[]>({
    queryKey: [...queryKeys.prs.detail(repoName, prId), 'commits'],
    queryFn: () =>
      apiClient.get<CommitSummary[]>(
        `/repos/${encodeURIComponent(repoName)}/prs/${encodeURIComponent(prId)}/commits`,
      ),
    staleTime: FREQUENT_STALE_TIME,
  });
  return data?.[0]?.commitId;
}

/**
 * Body for POST /repos/:name/prs/:id/comments. Either a top-level comment (body only) or
 * an inline comment carrying ALL FOUR location fields — the Comments API rejects a
 * partial inline set ("all-or-nothing").
 */
export type CreateCommentPayload =
  | { body: string }
  | { body: string; filePath: string; lineNumber: number; side: 'LEFT' | 'RIGHT'; commitId: string };

/**
 * Builds the create-comment payload. Produces an inline payload (with commitId) only when
 * a concrete filePath + a resolved head commit are available; otherwise a top-level
 * comment. This keeps the inline fields all-or-nothing and never sends a partial set.
 */
export function buildCreateCommentPayload(
  body: string,
  location: { filePath: string; lineNumber: number; side: 'LEFT' | 'RIGHT' } | null,
  headCommitId: string | undefined,
): CreateCommentPayload {
  if (location && location.filePath && headCommitId) {
    return {
      body,
      filePath: location.filePath,
      lineNumber: location.lineNumber,
      side: location.side,
      commitId: headCommitId,
    };
  }
  return { body };
}

interface CheckDetail {
  name: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'SUCCEEDED' | 'FAILED' | 'STOPPED';
  required: boolean;
  startTimestamp: string | null;
  completionTimestamp: string | null;
  durationSeconds: number | null;
  logsDeepLink: string | null;
}

/** A comment record from GET /repos/:name/prs/:id/comments. */
interface PrCommentFields {
  commentId: string;
  filePath?: string | null;
  lineNumber?: number | null;
  side?: 'LEFT' | 'RIGHT' | null;
  body: string;
  authorId?: string;
  authorDisplayName?: string;
  createdAt: string | number;
  outdated?: boolean;
  parentId?: string;
  parentCommentId?: string | null;
  resolved?: boolean;
  isAiReview?: boolean;
  dismissed?: boolean;
}

interface PrComment {
  comment: PrCommentFields;
  replies?: PrCommentFields[];
}

interface CommentsResponse {
  items: PrComment[];
  cursor?: string;
}

/**
 * Normalizes the production nested-replies response and the legacy flat shape used by
 * older clients/tests into the flat records consumed by the Comments tab.
 */
function normalizePrComments(data: CommentsResponse | PrComment[] | undefined): PrComment[] {
  const items = Array.isArray(data) ? data : data?.items ?? [];
  return items.flatMap((item) => [
    { comment: item.comment },
    ...(item.replies ?? []).map((reply) => ({
      comment: {
        ...reply,
        parentId: reply.parentId ?? reply.parentCommentId ?? item.comment.commentId,
      },
    })),
  ]);
}

function StatusBadge({ status }: { status: 'OPEN' | 'CLOSED' | 'MERGED' }) {
  const styles = {
    OPEN: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    CLOSED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    MERGED: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[status]}`}
      aria-label={`Status: ${status.toLowerCase()}`}
    >
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

function HeaderSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading pull request details" className="animate-pulse">
      <div className="h-8 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
      <div className="mt-3 flex flex-wrap gap-3">
        <div className="h-5 w-20 rounded-full bg-gray-200 dark:bg-gray-700" />
        <div className="h-5 w-32 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-5 w-48 rounded bg-gray-200 dark:bg-gray-700" />
      </div>
      <div className="mt-3 flex gap-3">
        <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-700" />
      </div>
    </div>
  );
}

function FileTreeSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading file tree" className="animate-pulse space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="h-4 w-4 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 flex-1 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      ))}
    </div>
  );
}

function DiffSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading diff" className="animate-pulse space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div className="h-4 w-1/2 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="mt-3 space-y-2">
            <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-3 w-5/6 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-3 w-4/6 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        </div>
      ))}
    </div>
  );
}

function CiSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading CI checks" className="animate-pulse space-y-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="h-4 w-4 rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      ))}
    </div>
  );
}

function SectionError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
      <p className="text-sm text-red-700 dark:text-red-300">{message}</p>
      <button
        onClick={onRetry}
        className="mt-2 rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
      >
        Retry
      </button>
    </div>
  );
}

function FileTreeSection({
  repoName,
  prId,
  selectedFile,
  onFileSelect,
}: {
  repoName: string;
  prId: string;
  selectedFile?: string;
  onFileSelect?: (filePath: string) => void;
}) {
  // Derive the file tree from the same /diff response — no separate /files call needed.
  const { data, isLoading, isError, refetch } = useQuery<DiffResponse>({
    queryKey: queryKeys.prs.diff(repoName, prId),
    queryFn: () => fetchDiff(repoName, prId),
    staleTime: FREQUENT_STALE_TIME,
  });

  if (isLoading) {
    return <FileTreeSkeleton />;
  }

  if (isError) {
    return (
      <SectionError
        message="Failed to load file tree."
        onRetry={() => refetch()}
      />
    );
  }

  // When the diff is not yet ready, show an empty tree (DiffSection shows the not-ready UI)
  const files = (data?.notReady ? [] : data?.files ?? []).map((f) => ({
    filePath: f.filePath,
    changeType: f.changeType,
    additions: f.additions,
    deletions: f.deletions,
  }));

  return (
    <FileTree
      files={files}
      selectedFile={selectedFile}
      onFileSelect={onFileSelect}
    />
  );
}

function DiffSection({
  repoName,
  prId,
  comments,
  onSubmitComment,
}: {
  repoName: string;
  prId: string;
  comments: InlineCommentData[];
  onSubmitComment: (params: {
    filePath: string;
    lineNumber: number;
    side: 'LEFT' | 'RIGHT';
    body: string;
  }) => Promise<void>;
}) {
  const [viewMode, setViewMode] = useState<'unified' | 'split'>('unified');

  const { data, isLoading, isError, refetch } = useQuery<DiffResponse>({
    queryKey: queryKeys.prs.diff(repoName, prId),
    queryFn: () => fetchDiff(repoName, prId),
    staleTime: FREQUENT_STALE_TIME,
  });

  if (isLoading) {
    return <DiffSkeleton />;
  }

  if (isError) {
    return (
      <SectionError
        message="Failed to load diff."
        onRetry={() => refetch()}
      />
    );
  }

  // Diff has been requested but not yet computed by CodeBuild — prompt the user to retry.
  if (data?.notReady) {
    return (
      <SectionError
        message="Diff is being computed, please retry in a moment."
        onRetry={() => refetch()}
      />
    );
  }

  const files = data?.files ?? [];

  if (files.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">No changes to display.</p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Diff ({files.length} file{files.length !== 1 ? 's' : ''})
        </h3>
        <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
          <button
            onClick={() => setViewMode('unified')}
            className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
              viewMode === 'unified'
                ? 'bg-brand-100 text-brand-800 dark:bg-brand-900 dark:text-brand-200'
                : 'text-content-secondary hover:text-content'
            }`}
          >
            Unified
          </button>
          <button
            onClick={() => setViewMode('split')}
            className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
              viewMode === 'split'
                ? 'bg-brand-100 text-brand-800 dark:bg-brand-900 dark:text-brand-200'
                : 'text-content-secondary hover:text-content'
            }`}
          >
            Split
          </button>
        </div>
      </div>
      <DiffWithComments
        files={files.map((f) => ({
          filePath: f.filePath,
          changeType: f.changeType,
          oldFilePath: f.oldFilePath,
          diffText: f.diffText,
          isBinary: f.isBinary,
          additions: f.additions,
          deletions: f.deletions,
        }))}
        viewMode={viewMode}
        comments={comments}
        onSubmitComment={onSubmitComment}
      />
    </div>
  );
}

function NewCommentForm({
  onSubmit,
  onCancel,
  isPending,
}: {
  onSubmit: (body: string) => Promise<void>;
  onCancel: () => void;
  isPending: boolean;
}) {
  const [body, setBody] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    await onSubmit(body.trim());
    setBody('');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <label htmlFor="new-comment-body" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Add a comment
      </label>
      <textarea
        id="new-comment-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-500"
        placeholder="Write a comment..."
        required
      />
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={isPending || !body.trim()}
          className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 dark:focus:ring-offset-gray-900"
        >
          {isPending ? 'Submitting...' : 'Submit'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:focus:ring-offset-gray-900"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function CommentsSection({ repoName, prId }: { repoName: string; prId: string }) {
  const queryClient = useQueryClient();
  const headCommitId = usePrHeadCommitId(repoName, prId);
  const [activeForm, setActiveForm] = useState<{
    filePath: string;
    lineNumber: number;
    side: 'LEFT' | 'RIGHT';
  } | null>(null);

  const { data, isLoading, isError, refetch } = useQuery<CommentsResponse | PrComment[]>({
    queryKey: queryKeys.comments.byPr(repoName, prId),
    queryFn: () =>
      apiClient.get<CommentsResponse | PrComment[]>(
        `/repos/${encodeURIComponent(repoName)}/prs/${encodeURIComponent(prId)}/comments`,
      ),
    staleTime: FREQUENT_STALE_TIME,
  });

  const submitCommentMutation = useMutation({
    mutationFn: (params: CreateCommentPayload) =>
      apiClient.post(
        `/repos/${encodeURIComponent(repoName)}/prs/${encodeURIComponent(prId)}/comments`,
        params,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.comments.byPr(repoName, prId) });
      setActiveForm(null);
    },
  });

  const submitReplyMutation = useMutation({
    mutationFn: (params: { parentId: string; body: string }) =>
      apiClient.post(`/comments/${encodeURIComponent(params.parentId)}/replies`, {
        body: params.body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.comments.byPr(repoName, prId) });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (commentId: string) =>
      apiClient.post(`/comments/${encodeURIComponent(commentId)}/resolve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.comments.byPr(repoName, prId) });
    },
  });

  const unresolveMutation = useMutation({
    mutationFn: (commentId: string) =>
      apiClient.post(`/comments/${encodeURIComponent(commentId)}/unresolve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.comments.byPr(repoName, prId) });
    },
  });

  const handleSubmitReply = useCallback(
    async (parentId: string, body: string) => {
      await submitReplyMutation.mutateAsync({ parentId, body });
    },
    [submitReplyMutation],
  );

  const handleResolve = useCallback(
    async (commentId: string) => {
      await resolveMutation.mutateAsync(commentId);
    },
    [resolveMutation],
  );

  const handleUnresolve = useCallback(
    async (commentId: string) => {
      await unresolveMutation.mutateAsync(commentId);
    },
    [unresolveMutation],
  );

  if (isLoading) {
    return (
      <div aria-busy="true" aria-label="Loading comments" className="animate-pulse space-y-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <div className="h-4 w-1/3 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="mt-2 h-3 w-2/3 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <SectionError
        message="Failed to load comments."
        onRetry={() => refetch()}
      />
    );
  }

  const comments = normalizePrComments(data);

  // Group comments into threads: root comments (no parentId) and their replies
  const rootComments = comments.filter((c) => !c.comment.parentId);
  const repliesByParent = new Map<string, InlineCommentData[]>();
  for (const comment of comments) {
    if (comment.comment.parentId) {
      const existing = repliesByParent.get(comment.comment.parentId) ?? [];
      existing.push(mapPrCommentToInlineData(comment));
      repliesByParent.set(comment.comment.parentId, existing);
    }
  }

  if (rootComments.length === 0 && !activeForm) {
    return (
      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Comments</h3>
          <button
            onClick={() => setActiveForm({ filePath: '', lineNumber: 0, side: 'RIGHT' })}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            New Comment
          </button>
        </div>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          No comments yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Comments ({rootComments.length})
        </h3>
        {!activeForm && (
          <button
            onClick={() => setActiveForm({ filePath: '', lineNumber: 0, side: 'RIGHT' })}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            New Comment
          </button>
        )}
      </div>
      {rootComments.map((comment) => (
        <CommentThread
          key={comment.comment.commentId}
          rootComment={mapPrCommentToInlineData(comment)}
          replies={repliesByParent.get(comment.comment.commentId) ?? []}
          onSubmitReply={handleSubmitReply}
          onResolve={handleResolve}
          onUnresolve={handleUnresolve}
        />
      ))}
      {activeForm && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <NewCommentForm
            onSubmit={async (body) => {
              // The "New Comment" button creates a top-level (non-inline) comment, so no
              // location fields are sent. Inline comments are created from the diff gutter.
              const location = activeForm.filePath
                ? { filePath: activeForm.filePath, lineNumber: activeForm.lineNumber, side: activeForm.side }
                : null;
              await submitCommentMutation.mutateAsync(
                buildCreateCommentPayload(body, location, headCommitId),
              );
            }}
            onCancel={() => setActiveForm(null)}
            isPending={submitCommentMutation.isPending}
          />
        </div>
      )}
    </div>
  );
}

function mapPrCommentToInlineData(comment: PrComment): InlineCommentData {
  const fields = comment.comment;
  return {
    id: fields.commentId,
    filePath: fields.filePath ?? '',
    lineNumber: fields.lineNumber ?? 0,
    side: fields.side ?? 'RIGHT',
    body: fields.body,
    author: { displayName: fields.authorDisplayName ?? fields.authorId ?? 'Unknown user' },
    createdAt: typeof fields.createdAt === 'number'
      ? new Date(fields.createdAt).toISOString()
      : fields.createdAt,
    outdated: fields.outdated ?? false,
    parentId: fields.parentId ?? fields.parentCommentId ?? undefined,
    resolved: fields.resolved ?? false,
    isAiReview: fields.isAiReview,
    dismissed: fields.dismissed,
  };
}

function CommitsSection({ repoName, prId }: { repoName: string; prId: string }) {
  const { data, isLoading, isError, refetch } = useQuery<CommitSummary[]>({
    queryKey: [...queryKeys.prs.detail(repoName, prId), 'commits'],
    queryFn: () =>
      apiClient.get<CommitSummary[]>(
        `/repos/${encodeURIComponent(repoName)}/prs/${encodeURIComponent(prId)}/commits`,
      ),
    staleTime: FREQUENT_STALE_TIME,
  });

  if (isLoading) {
    return (
      <div aria-busy="true" aria-label="Loading commits" className="animate-pulse space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="h-4 w-16 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 flex-1 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <SectionError
        message="Failed to load commits."
        onRetry={() => refetch()}
      />
    );
  }

  const commits = data ?? [];

  if (commits.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">No commits found.</p>
    );
  }

  return (
    <ul className="space-y-2" aria-label="Commits">
      {commits.map((commit) => (
        <li
          key={commit.commitId}
          className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2"
        >
          <a
            href={`https://eu-west-2.console.aws.amazon.com/codesuite/codecommit/repositories/${encodeURIComponent(repoName)}/commit/${commit.commitId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 font-mono text-xs text-blue-600 hover:underline dark:text-blue-400"
            title="View commit in CodeCommit"
          >
            {commit.commitId.slice(0, 7)}
          </a>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
              {commit.message.split('\n')[0]}
            </p>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {commit.authorName} · {formatRelativeTime(commit.authorDate)}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function CiSection({ repoName, prId }: { repoName: string; prId: string }) {
  const { data, isLoading, isError, refetch } = useQuery<ChecksResponse>({
    queryKey: queryKeys.prs.checks(repoName, prId),
    queryFn: () =>
      apiClient.get<ChecksResponse>(
        `/repos/${encodeURIComponent(repoName)}/prs/${encodeURIComponent(prId)}/checks`,
      ),
    staleTime: FREQUENT_STALE_TIME,
  });

  if (isLoading) {
    return <CiSkeleton />;
  }

  if (isError) {
    return (
      <SectionError
        message="Failed to load CI checks."
        onRetry={() => refetch()}
      />
    );
  }

  const checks = data?.checks ?? [];

  if (checks.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {data?.message ?? 'No CI checks configured.'}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {data?.aggregateStatus && (
        <div className="flex items-center gap-2 mb-2">
          <CiStatusIcon status={data.aggregateStatus} />
          <span className="text-sm font-medium text-content">
            {formatCiStatus(data.aggregateStatus)}
          </span>
        </div>
      )}
      <ul className="space-y-1.5" aria-label="CI check results">
        {checks.map((check) => (
          <li key={check.name} className="flex items-center gap-2">
            <CiStatusIcon status={check.status} />
            <span className="text-sm text-content-secondary">{check.name}</span>
            {check.required && (
              <span className="rounded bg-warning/15 px-1.5 py-0.5 text-xs font-medium text-warning">
                required
              </span>
            )}
            {check.durationSeconds != null && (
              <span className="text-xs text-content-tertiary">
                {check.durationSeconds}s
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CiStatusIcon({ status }: { status: string }) {
  switch (status.toUpperCase()) {
    case 'SUCCEEDED':
    case 'PASSED':
    case 'ALL_PASSING':
      return (
        <svg className="h-4 w-4 text-success" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      );
    case 'FAILED':
    case 'SOME_FAILING':
      return (
        <svg className="h-4 w-4 text-danger" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      );
    case 'IN_PROGRESS':
    case 'RUNNING':
      return (
        <svg className="h-4 w-4 animate-spin text-brand-600" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      );
    default:
      return (
        <svg className="h-4 w-4 text-content-tertiary" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
        </svg>
      );
  }
}

function formatCiStatus(status: string): string {
  switch (status.toUpperCase()) {
    case 'ALL_PASSING': return 'All checks passed';
    case 'SOME_FAILING': return 'Some checks failed';
    case 'IN_PROGRESS': return 'Checks running';
    case 'PENDING': return 'Checks pending';
    default: return status;
  }
}

function PrActions({ repoName, prId, pr }: { repoName: string; prId: string; pr: PrDetail }) {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [mergeStrategy, setMergeStrategy] = useState<'THREE_WAY_MERGE' | 'SQUASH_MERGE' | 'FAST_FORWARD_MERGE'>('THREE_WAY_MERGE');
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Determine if current user has already approved
  const currentUserApproved = pr.approvals?.some(
    (a) => a.displayName === user?.displayName,
  ) ?? false;

  const approveMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/repos/${encodeURIComponent(repoName)}/prs/${encodeURIComponent(prId)}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.prs.detail(repoName, prId) });
    },
  });

  const revokeApprovalMutation = useMutation({
    mutationFn: () =>
      apiClient.delete(`/repos/${encodeURIComponent(repoName)}/prs/${encodeURIComponent(prId)}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.prs.detail(repoName, prId) });
    },
  });

  const mergeMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/repos/${encodeURIComponent(repoName)}/prs/${encodeURIComponent(prId)}/merge`, {
        strategy: mergeStrategy,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.prs.detail(repoName, prId) });
      setShowMergeConfirm(false);
    },
  });

  const closeMutation = useMutation({
    mutationFn: () =>
      apiClient.delete(`/repos/${encodeURIComponent(repoName)}/prs/${encodeURIComponent(prId)}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.prs.detail(repoName, prId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.prs.list(repoName) });
      setShowCloseConfirm(false);
    },
  });

  return (
    <div className="mt-4 flex flex-wrap items-center gap-3">
      {/* Approve or Unapprove based on current state */}
      {currentUserApproved ? (
        <button
          onClick={() => revokeApprovalMutation.mutate()}
          disabled={revokeApprovalMutation.isPending}
          className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:focus:ring-offset-gray-900"
        >
          {revokeApprovalMutation.isPending ? 'Revoking...' : 'Unapprove'}
        </button>
      ) : (
        <button
          onClick={() => approveMutation.mutate()}
          disabled={approveMutation.isPending}
          className="inline-flex items-center rounded-lg border border-green-600 bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 dark:border-green-500 dark:bg-green-900/20 dark:text-green-300 dark:hover:bg-green-900/40 dark:focus:ring-offset-gray-900"
        >
          {approveMutation.isPending ? 'Approving...' : 'Approve'}
        </button>
      )}

      {/* Merge */}
      {!showMergeConfirm ? (
        <button
          onClick={() => setShowMergeConfirm(true)}
          className="inline-flex items-center rounded-lg bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 dark:focus:ring-offset-gray-900"
        >
          Merge
        </button>
      ) : (
        <div className="flex items-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 dark:border-purple-800 dark:bg-purple-900/20">
          <select
            value={mergeStrategy}
            onChange={(e) => setMergeStrategy(e.target.value as typeof mergeStrategy)}
            className="rounded border border-purple-300 bg-white px-2 py-1 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-purple-700 dark:bg-gray-900 dark:text-gray-100"
            aria-label="Merge strategy"
          >
            <option value="THREE_WAY_MERGE">3-way merge</option>
            <option value="SQUASH_MERGE">Squash merge</option>
            <option value="FAST_FORWARD_MERGE">Fast forward</option>
          </select>
          <button
            onClick={() => mergeMutation.mutate()}
            disabled={mergeMutation.isPending}
            className="rounded-md bg-purple-600 px-3 py-1 text-xs font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
          >
            {mergeMutation.isPending ? 'Merging...' : 'Confirm Merge'}
          </button>
          <button
            onClick={() => setShowMergeConfirm(false)}
            className="rounded-md px-2 py-1 text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Close PR */}
      {!showCloseConfirm ? (
        <button
          onClick={() => setShowCloseConfirm(true)}
          className="inline-flex items-center rounded-lg border border-red-600 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 dark:border-red-500 dark:bg-red-900/20 dark:text-red-300 dark:hover:bg-red-900/40 dark:focus:ring-offset-gray-900"
        >
          Close PR
        </button>
      ) : (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-900/20">
          <span className="text-xs text-red-700 dark:text-red-300">
            Close this pull request without merging?
          </span>
          <button
            onClick={() => closeMutation.mutate()}
            disabled={closeMutation.isPending}
            className="rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
          >
            {closeMutation.isPending ? 'Closing...' : 'Confirm Close'}
          </button>
          <button
            onClick={() => setShowCloseConfirm(false)}
            className="rounded-md px-2 py-1 text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Error messages */}
      {approveMutation.isError && (
        <p className="w-full text-xs text-red-600 dark:text-red-400" role="alert">
          {formatActionError(approveMutation.error, 'approve')}
        </p>
      )}
      {revokeApprovalMutation.isError && (
        <p className="w-full text-xs text-red-600 dark:text-red-400" role="alert">
          {formatActionError(revokeApprovalMutation.error, 'revoke approval')}
        </p>
      )}
      {mergeMutation.isError && (
        <p className="w-full text-xs text-red-600 dark:text-red-400" role="alert">
          {formatActionError(mergeMutation.error, 'merge')}
        </p>
      )}
      {closeMutation.isError && (
        <p className="w-full text-xs text-red-600 dark:text-red-400" role="alert">
          {formatActionError(closeMutation.error, 'close')}
        </p>
      )}
    </div>
  );
}

/** Extracts a user-friendly error message from API errors for PR actions */
function formatActionError(error: unknown, action: string): string {
  if (error instanceof ApiError) {
    const body = error.body as { error: { code?: string, message?: string; requestId?: string }} | undefined;
    const message = body?.error?.message ?? '';

    // Map common backend error codes/messages to friendly text
    if (error.status === 403) {
      if (message.toLowerCase().includes('cannot approve your own')) return 'You cannot approve your own pull request.';
      return message || 'You do not have permission to perform this action.';
    }
    if (error.status === 409) {
      if (message.toLowerCase().includes('conflict')) return 'Merge conflicts exist that must be resolved first.';
      if (message.toLowerCase().includes('closed')) return 'This pull request is already closed.';
      if (message.toLowerCase().includes('changed')) return 'The source branch has changed. Please refresh and try again.';
      return message || 'A conflict prevented this action.';
    }
    if (error.status === 400) {
      return message || 'Invalid request. Please check your inputs.';
    }
    if (message) return message;
  }
  return `Failed to ${action}. Please try again.`;
}

export function PrDetailPage() {
  const { name, id, repoName: repoNameParam, projectKey } = useParams<{ name: string; id: string; repoName: string; projectKey: string }>();
  const repoName = repoNameParam ?? name ?? '';
  const prId = id ?? '';
  const repoPath = projectKey
    ? `/projects/${encodeURIComponent(projectKey)}/repos/${encodeURIComponent(repoName)}`
    : `/repos/${encodeURIComponent(repoName)}`;
  const [selectedFile, setSelectedFile] = useState<string | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<'changes' | 'commits' | 'comments' | 'ci'>('changes');

  const queryClient = useQueryClient();
  const headCommitId = usePrHeadCommitId(repoName, prId);

  // Mutation to submit inline comments from the diff gutter
  const inlineCommentMutation = useMutation({
    mutationFn: (params: CreateCommentPayload) =>
      apiClient.post(
        `/repos/${encodeURIComponent(repoName)}/prs/${encodeURIComponent(prId)}/comments`,
        params,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.comments.byPr(repoName, prId) });
    },
  });

  // Scroll diff to the selected file section
  const handleFileSelect = useCallback((filePath: string) => {
    setSelectedFile(filePath);
    // Attempt to scroll to the diff section for this file
    const fileElement = document.getElementById(`diff-file-${CSS.escape(filePath)}`);
    if (fileElement) {
      fileElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Fetch PR detail
  const {
    data: pr,
    isLoading: prLoading,
    isError: prError,
    error: prErrorObj,
  } = useQuery<PrDetail>({
    queryKey: queryKeys.prs.detail(repoName, prId),
    queryFn: () =>
      apiClient.get<PrDetail>(
        `/repos/${encodeURIComponent(repoName)}/prs/${encodeURIComponent(prId)}`,
      ),
    staleTime: FREQUENT_STALE_TIME,
    enabled: !!repoName && !!prId,
  });

  // Fetch comments while the PR detail is open so inline review comments are available
  // on the Changes tab as well as in the threaded Comments tab.
  const { data: commentsData } = useQuery<CommentsResponse | PrComment[]>({
    queryKey: queryKeys.comments.byPr(repoName, prId),
    queryFn: () =>
      apiClient.get<CommentsResponse | PrComment[]>(
        `/repos/${encodeURIComponent(repoName)}/prs/${encodeURIComponent(prId)}/comments`,
      ),
    staleTime: FREQUENT_STALE_TIME,
    enabled: !!repoName && !!prId,
  });

  const inlineComments = normalizePrComments(commentsData)
    .map(mapPrCommentToInlineData)
    .filter((comment) => comment.filePath.length > 0 && comment.lineNumber > 0);

  const handleSubmitInlineComment = useCallback(
    async (params: {
      filePath: string;
      lineNumber: number;
      side: 'LEFT' | 'RIGHT';
      body: string;
    }) => {
      if (!headCommitId) {
        throw new Error('Commit information is still loading; please retry in a moment.');
      }
      await inlineCommentMutation.mutateAsync(
        buildCreateCommentPayload(params.body, params, headCommitId),
      );
    },
    [headCommitId, inlineCommentMutation],
  );

  // Handle 404 - PR Not Found
  if (prError && prErrorObj instanceof ApiError && prErrorObj.status === 404) {
    return (
      <main id="main-content" className="mx-auto max-w-7xl px-4 py-6">
        <div className="py-12 text-center" role="alert">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Pull Request Not Found
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            The pull request you are looking for does not exist.
          </p>
          <Link
            to={repoPath}
            className="mt-4 inline-block rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            Back to Repository
          </Link>
        </div>
      </main>
    );
  }

  // Handle other PR detail errors
  if (prError && !prLoading) {
    return (
      <main id="main-content" className="mx-auto max-w-7xl px-4 py-6">
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          <p className="font-medium">Failed to load pull request</p>
          <p className="mt-1 text-sm">
            {prErrorObj instanceof Error ? prErrorObj.message : 'An unexpected error occurred'}
          </p>
        </div>
      </main>
    );
  }

  const tabs = [
    { id: 'changes' as const, label: 'Changes' },
    { id: 'commits' as const, label: 'Commits' },
    { id: 'comments' as const, label: 'Comments' },
    { id: 'ci' as const, label: 'CI Status' },
  ];

  return (
    <main id="main-content" className="mx-auto max-w-7xl px-4 py-6">
      {/* PR Header */}
      {prLoading ? (
        <HeaderSkeleton />
      ) : (
        pr && (
          <header className="border-b border-gray-200 pb-6 dark:border-gray-700">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {truncateTitle(pr.title, DETAIL_TITLE_MAX)}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <StatusBadge status={pr.status} />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {pr.authorDisplayName ?? pr.authorId}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                <span className="font-mono text-xs">{pr.sourceBranch}</span>
                {' → '}
                <span className="font-mono text-xs">{pr.destinationBranch}</span>
              </span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span aria-label={`Created ${formatRelativeTime(pr.createdAt)}`}>
                Created {formatRelativeTime(pr.createdAt)}
              </span>
              <span aria-label={`Updated ${formatRelativeTime(pr.lastActivityDate)}`}>
                Updated {formatRelativeTime(pr.lastActivityDate)}
              </span>
              <span aria-label={`${pr.approvalCount} approval${pr.approvalCount !== 1 ? 's' : ''}`}>
                {pr.approvalCount} approval{pr.approvalCount !== 1 ? 's' : ''}
              </span>
            </div>
          </header>
        )
      )}

      {/* Actions panel — Approve & Merge buttons for open PRs */}
      {pr && pr.status === 'OPEN' && (
        <PrActions repoName={repoName} prId={prId} pr={pr} />
      )}

      {/* Tab navigation */}
      <nav className="mt-6 border-b border-gray-200 dark:border-gray-700" aria-label="PR sections">
        <ul className="-mb-px flex gap-6" role="tablist">
          {tabs.map((tab) => (
            <li key={tab.id} role="presentation">
              <button
                role="tab"
                id={`tab-${tab.id}`}
                aria-selected={activeTab === tab.id}
                aria-controls={`panel-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`inline-block border-b-2 pb-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                  activeTab === tab.id
                    ? 'border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-300'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Tab panels */}
      <div className="mt-6">
        {/* Changes tab */}
        <div
          role="tabpanel"
          id="panel-changes"
          aria-labelledby="tab-changes"
          hidden={activeTab !== 'changes'}
        >
          {activeTab === 'changes' && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
              {/* File Tree Panel */}
              <aside>
                <h2 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Changed Files
                </h2>
                <ErrorBoundary
                  fallback={
                    <SectionError
                      message="An error occurred in the file tree section."
                      onRetry={() => window.location.reload()}
                    />
                  }
                >
                  <FileTreeSection
                    repoName={repoName}
                    prId={prId}
                    selectedFile={selectedFile}
                    onFileSelect={handleFileSelect}
                  />
                </ErrorBoundary>
              </aside>

              {/* Diff */}
              <div>
                <ErrorBoundary
                  fallback={
                    <SectionError
                      message="An error occurred in the diff section."
                      onRetry={() => window.location.reload()}
                    />
                  }
                >
                  <DiffSection
                    repoName={repoName}
                    prId={prId}
                    comments={inlineComments}
                    onSubmitComment={handleSubmitInlineComment}
                  />
                </ErrorBoundary>
              </div>
            </div>
          )}
        </div>

        {/* Commits tab */}
        <div
          role="tabpanel"
          id="panel-commits"
          aria-labelledby="tab-commits"
          hidden={activeTab !== 'commits'}
        >
          {activeTab === 'commits' && (
            <ErrorBoundary
              fallback={
                <SectionError
                  message="An error occurred in the commits section."
                  onRetry={() => window.location.reload()}
                />
              }
            >
              <CommitsSection repoName={repoName} prId={prId} />
            </ErrorBoundary>
          )}
        </div>

        {/* Comments tab */}
        <div
          role="tabpanel"
          id="panel-comments"
          aria-labelledby="tab-comments"
          hidden={activeTab !== 'comments'}
        >
          {activeTab === 'comments' && (
            <ErrorBoundary
              fallback={
                <SectionError
                  message="An error occurred in the comments section."
                  onRetry={() => window.location.reload()}
                />
              }
            >
              <CommentsSection repoName={repoName} prId={prId} />
            </ErrorBoundary>
          )}
        </div>

        {/* CI Status tab */}
        <div
          role="tabpanel"
          id="panel-ci"
          aria-labelledby="tab-ci"
          hidden={activeTab !== 'ci'}
        >
          {activeTab === 'ci' && (
            <ErrorBoundary
              fallback={
                <SectionError
                  message="An error occurred in the CI section."
                  onRetry={() => window.location.reload()}
                />
              }
            >
              <CiSection repoName={repoName} prId={prId} />
            </ErrorBoundary>
          )}
        </div>
      </div>
    </main>
  );
}
